import React, { useEffect } from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";

import NotoSansTamilBold from "../assests/fonts/NotoSansTamil-Bold.ttf";
import NotoSansTamilRegular from "../assests/fonts/NotoSansTamil-Regular.ttf";

// ---------------- FONT REGISTER (Safe + One-Time) ----------------
let fontsRegistered = false;

const registerFonts = () => {
  if (fontsRegistered) return;
  try {
    Font.register({
      family: "NotoSansTamil",
      fonts: [
        { src: NotoSansTamilRegular, fontWeight: "normal" },
        { src: NotoSansTamilBold, fontWeight: "bold" },
      ],
    });
    fontsRegistered = true;
  } catch (err) {
    console.warn("Font registration failed", err);
  }
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 30,
    paddingBottom: 60,
    paddingHorizontal: 40,
    fontFamily: "NotoSansTamil",
    fontSize: 10,
    backgroundColor: "#ffffff",
  },
  header: { textAlign: "center", marginBottom: 15 },
  companyName: { fontSize: 18, fontWeight: "bold", color: "#1e40af" },
  reportTitle: { fontSize: 14, marginTop: 5, fontWeight: "bold" },
  dateRange: { fontSize: 10, color: "#475569", marginTop: 3 },

  table: {
    width: "100%",
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 6,
    overflow: "hidden",
    marginTop: 10,
  },
  headerRow: {
    flexDirection: "row",
    backgroundColor: "#1e40af",
    color: "white",
  },
  row: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
  },
  rowAlt: { backgroundColor: "#f8fafc" },

  cell: { paddingVertical: 6, paddingHorizontal: 6, fontSize: 9, textAlign: "center" },
  cellLeft: { textAlign: "left", paddingLeft: 12 },
  cellRight: { textAlign: "right", paddingRight: 12 },
  headerCell: { fontWeight: "bold", paddingVertical: 8 },

  totalRow: { backgroundColor: "#1e3a8a", color: "white", fontWeight: "bold" },

  footer: {
    position: "absolute",
    bottom: 25,
    left: 40,
    right: 40,
    textAlign: "center",
    fontSize: 9,
    borderTopWidth: 1,
    borderTopColor: "#e5e7eb",
    paddingTop: 6,
    color: "#64748b",
  },
});

const safeNumber = (val) => (isNaN(Number(val)) ? 0 : Number(val));

const formatCurrency = (value) =>
  `₹${safeNumber(value).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;

const OverallSalaryPDF = ({ data = [], fromDate, toDate }) => {
  registerFonts();

  // Normalize dataset
  let normalizedData = Array.isArray(data)
    ? data
    : data && typeof data === "object"
    ? Object.values(data)
    : [];

  normalizedData = normalizedData
    .filter((x) => x)
    .map((item, index) => ({
      staffId: item.staffId || index,
      staff_name: item.staff_name || `Staff ${index + 1}`,
      daysWorked: safeNumber(item.daysWorked),
      halfDays: safeNumber(item.halfDays),
      totalGross: safeNumber(item.totalGross),
      totalNet: safeNumber(item.totalNet),
      finalPayable: safeNumber(item.finalPayable),
      advanceDeductionThisWeek: safeNumber(item.advanceDeductionThisWeek),
      advanceBalance: safeNumber(item.advanceBalance),
    }))
    .sort((a, b) => a.staff_name.localeCompare(b.staff_name));

  const hasData = normalizedData.length > 0;

  const formatDate = (date) => {
    if (!date) return "All Time";
    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const totals = {
    gross: normalizedData.reduce((s, i) => s + i.totalGross, 0),
    net: normalizedData.reduce((s, i) => s + i.totalNet, 0),
    final: normalizedData.reduce((s, i) => s + i.finalPayable, 0),
    recover: normalizedData.reduce((s, i) => s + i.advanceDeductionThisWeek, 0),
    full: normalizedData.reduce((s, i) => s + i.daysWorked, 0),
    half: normalizedData.reduce((s, i) => s + i.halfDays, 0),
  };

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        {/* HEADER */}
        <View style={styles.header}>
          <Text style={styles.companyName}>MKS</Text>
          <Text style={styles.reportTitle}>Overall Staff Salary Report</Text>
          <Text style={styles.dateRange}>
            Period: {formatDate(fromDate)} – {formatDate(toDate)}
          </Text>
        </View>

        {!hasData && (
          <View>
            <Text
              style={{
                textAlign: "center",
                color: "red",
                fontSize: 12,
                marginTop: 20,
              }}
            >
              No valid staff records found
            </Text>
          </View>
        )}

        {hasData && (
          <View style={styles.table}>
            {/* HEADER */}
            <View style={styles.headerRow} fixed>

              <Text style={[styles.cell, styles.headerCell, { width: 35 }]}>S.No</Text>
                <Text style={[styles.cell, styles.headerCell, styles.cellRight, { width: 95 }]}>
                Advance Bal
              </Text>
              <Text style={[styles.cell, styles.headerCell, styles.cellLeft, { flex: 3 }]}>
                Staff Name
              </Text>
              <Text style={[styles.cell, styles.headerCell, { width: 55 }]}>Full</Text>
              <Text style={[styles.cell, styles.headerCell, { width: 55 }]}>Half</Text>
              {/* <Text style={[styles.cell, styles.headerCell, styles.cellRight, { width: 90 }]}>
                Gross
              </Text>
              <Text style={[styles.cell, styles.headerCell, styles.cellRight, { width: 90 }]}>
                Net
              </Text>
              <Text style={[styles.cell, styles.headerCell, styles.cellRight, { width: 95 }]}>
                Recovery
              </Text>
              <Text style={[styles.cell, styles.headerCell, styles.cellRight, { width: 100 }]}>
                Final Pay
              </Text> */}
            
            </View>

            {/* ROWS */}
            {normalizedData.map((item, i) => (
              <View key={i} style={[styles.row, i % 2 && styles.rowAlt]}>
                <Text style={[styles.cell, { width: 35 }]}>{i + 1}</Text>
                 <Text style={[styles.cell, styles.cellRight, { width: 95 }]}>
                  {formatCurrency(item.advanceBalance)}
                </Text>
                <Text style={[styles.cell, styles.cellLeft, { flex: 3 }]}>
                  {item.staff_name}
                </Text>
                <Text style={[styles.cell, { width: 55 }]}>{item.daysWorked}</Text>
                <Text style={[styles.cell, { width: 55 }]}>{item.halfDays}</Text>
                {/* <Text style={[styles.cell, styles.cellRight, { width: 90 }]}>
                  {formatCurrency(item.totalGross)}
                </Text>
                <Text style={[styles.cell, styles.cellRight, { width: 90 }]}>
                  {formatCurrency(item.totalNet)}
                </Text>
                <Text style={[styles.cell, styles.cellRight, { width: 95 }]}>
                  {formatCurrency(item.advanceDeductionThisWeek)}
                </Text>
                <Text style={[styles.cell, styles.cellRight, { width: 100 }]}>
                  {formatCurrency(item.finalPayable)}
                </Text> */}
               
              </View>
            ))}

            {/* TOTAL ROW */}
            <View style={[styles.row, styles.totalRow]}>
              <Text style={[styles.cell, { width: 35 }]}></Text>
              <Text style={[styles.cell, styles.cellRight, { width: 95 }]}>
                —
              </Text>
              <Text style={[styles.cell, styles.cellLeft, { flex: 3 }]}>
                GRAND TOTAL
              </Text>
              <Text style={[styles.cell, { width: 55 }]}>{totals.full}</Text>
              <Text style={[styles.cell, { width: 55 }]}>{totals.half}</Text>
              {/* <Text style={[styles.cell, styles.cellRight, { width: 90 }]}>
                {formatCurrency(totals.gross)}
              </Text>
              <Text style={[styles.cell, styles.cellRight, { width: 90 }]}>
                {formatCurrency(totals.net)}
              </Text>
              <Text style={[styles.cell, styles.cellRight, { width: 95 }]}>
                {formatCurrency(totals.recover)}
              </Text>
              <Text style={[styles.cell, styles.cellRight, { width: 100 }]}>
                {formatCurrency(totals.final)}
              </Text> */}
              
            </View>
          </View>
        )}

        {/* FOOTER */}
        <View style={styles.footer} fixed>
          <Text>
            Generated on {new Date().toLocaleDateString("en-GB")} | MKS | Confidential
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default OverallSalaryPDF;
